import { useEffect, useState, useRef } from 'react';
import { FileText, Clipboard, File, FileBarChart, Eye, Trash2, Bot } from 'lucide-react';
import { knowledgeBase as kbApi } from '../../utils/api';
import SkeletonLoader from '../../components/shared/SkeletonLoader';

// TODO: POST /api/documenti/genera/ — Sprint 3 (Celery + Claude API)

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

const SLOTS = [
  { tipo: 'preventivo',      label: 'Preventivo',      color: 'var(--zone-blue)',   bg: tint('var(--zone-blue)', 10) },
  { tipo: 'contratto',       label: 'Contratto',       color: 'var(--zone-amber)',  bg: tint('var(--zone-amber)', 10) },
  { tipo: 'fattura',         label: 'Fattura',          color: 'var(--ok)',          bg: tint('var(--ok)', 10) },
  { tipo: 'carta_intestata', label: 'Carta intestata',  color: 'var(--zone-amber)',  bg: tint('var(--zone-amber)', 10) },
  { tipo: 'report_cantiere', label: 'Report cantiere', color: 'var(--zone-violet)', bg: tint('var(--zone-violet)', 10) },
];

/* ── Icons ─────────────────────────────────────────────────────────────── */
const FileIcon = ({ stroke = 'var(--ok)', size = 20 }) => (
  <File size={size} color={stroke} strokeWidth={2} />
);
const FileTextIcon = ({ stroke = 'var(--zone-blue)', size = 20 }) => (
  <FileText size={size} color={stroke} strokeWidth={2} />
);
const ClipboardIcon = ({ stroke = 'var(--zone-amber)', size = 20 }) => (
  <Clipboard size={size} color={stroke} strokeWidth={2} />
);
const EyeIcon = () => (
  <Eye size={14} strokeWidth={2} />
);
const TrashIcon = () => (
  <Trash2 size={14} strokeWidth={2} />
);

const ReportIcon = ({ stroke = 'var(--zone-violet)', size = 20 }) => (
  <FileBarChart size={size} color={stroke} strokeWidth={2} />
);

const SLOT_ICONS = {
  preventivo: FileTextIcon,
  contratto: ClipboardIcon,
  fattura: FileIcon,
  carta_intestata: FileIcon,
  report_cantiere: ReportIcon,
};

const fmtData = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/* ── Styles ────────────────────────────────────────────────────────────── */
const cardBase = {
  background: 'var(--ds-bg)',
  borderRadius: 'var(--radius-card)',
  padding: '18px 16px',
  minHeight: 180,
  display: 'flex',
  flexDirection: 'column',
};

const actionBtn = {
  width: 26,
  height: 26,
  borderRadius: 'var(--radius-base)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--ds-border)',
  background: 'transparent',
  color: 'var(--ds-muted)',
  cursor: 'pointer',
  transition: 'background 0.12s, color 0.12s',
};

export default function KnowledgeBase({ showToast }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAI, setShowAI]       = useState(false);
  const fileRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await kbApi.list();
      setTemplates(data.results || data);
    } catch {
      showToast('Errore caricamento template', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const allByTipo = (tipo) => templates.filter((t) => t.tipo === tipo);

  const handleUpload = async (tipo, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('nome', file.name.replace(/\.[^.]+$/, ''));
    fd.append('tipo', tipo);
    try {
      await kbApi.upload(fd);
      showToast('Template caricato', 'success');
      load();
    } catch {
      showToast('Errore upload', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await kbApi.delete(id);
      showToast('Template eliminato', 'success');
      load();
    } catch {
      showToast('Errore eliminazione', 'error');
    }
  };

  if (loading) return <SkeletonLoader type="card" count={4} />;

  const nessunTemplate = templates.length === 0;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)', marginBottom: 4 }}>I tuoi modelli aziendali</div>
        <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>Carica i tuoi template. L'AI li userà per generare documenti su richiesta.</div>
      </div>

      {/* Slot cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 28 }}>
        {SLOTS.map(({ tipo, label, color, bg }) => {
          const docs = allByTipo(tipo);
          const hasDocs = docs.length > 0;
          const Icon = SLOT_ICONS[tipo] || FileIcon;
          return (
            <div key={tipo} style={{
              ...cardBase,
              border: hasDocs ? '1px solid var(--ds-border)' : '2px dashed var(--ds-border)',
              boxShadow: hasDocs ? CARD_SHADOW : 'none',
            }}>
              <input
                ref={(el) => { if (el) fileRefs.current[tipo] = el; }}
                type="file"
                accept=".pdf,.docx,.doc"
                style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files[0]) handleUpload(tipo, e.target.files[0]); e.target.value = ''; }}
              />

              {/* Header dello slot — sempre uguale */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 'var(--radius-base)', background: bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon stroke={color} size={20} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>{label}</div>
                  <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>
                    {hasDocs ? `${docs.length} modell${docs.length === 1 ? 'o' : 'i'}` : 'Nessun modello'}
                  </div>
                </div>
              </div>

              {/* Lista documenti */}
              {hasDocs && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  {docs.map((tpl) => (
                    <div key={tpl.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 10px', borderRadius: 'var(--radius-base)',
                      background: 'var(--chip)', gap: 8,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.nome}</div>
                        <div style={{ fontSize: 10, color: 'var(--ds-muted)' }}>{fmtData(tpl.data_upload)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {tpl.file_url && (
                          <a href={tpl.file_url} target="_blank" rel="noopener noreferrer" title="Anteprima" style={{ ...actionBtn, width: 26, height: 26, textDecoration: 'none' }}>
                            <EyeIcon />
                          </a>
                        )}
                        <button
                          title="Elimina"
                          onClick={() => handleDelete(tpl.id)}
                          style={actionBtn}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ds-danger)'; e.currentTarget.style.borderColor = 'var(--ds-danger)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ds-muted)'; e.currentTarget.style.borderColor = 'var(--ds-border)'; }}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Bottone carica — sempre visibile in fondo */}
              <div style={{ marginTop: 'auto' }}>
                <button onClick={() => fileRefs.current[tipo]?.click()} style={{ padding: '7px 14px', borderRadius: 'var(--radius-base)', border: 'none', background: hasDocs ? 'var(--chip)' : 'var(--brand)', color: hasDocs ? 'var(--ds-text)' : '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 500, transition: 'background 0.12s' }}>+ Carica modello</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Banner come funziona — solo se nessun template */}
      {nessunTemplate && (
        <div style={{ background: 'var(--chip)', borderRadius: 'var(--radius-card)', padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)', marginBottom: 14 }}>Come funziona</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { n: '1', title: 'Upload', desc: 'Carica i tuoi template Word o PDF' },
              { n: '2', title: 'WhatsApp', desc: "Chiedi all'AI via WhatsApp di creare un documento" },
              { n: '3', title: 'AI genera', desc: 'Claude compila il template con i dati del cantiere' },
            ].map((s) => (
              <div key={s.n} style={{ flex: 1, minWidth: 140, textAlign: 'center' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: tint('var(--brand)', 15), color: 'var(--brand)', fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>{s.n}</div>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)', marginBottom: 2 }}>{s.title}</div>
                <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Box genera documento — placeholder */}
      <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW, padding: '20px 24px' }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)', marginBottom: 4 }}>Genera documento ora</div>
        <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 16 }}>Puoi fare la stessa richiesta via WhatsApp</div>
        <textarea
          placeholder={"Es. Crea un preventivo per il cantiere Rossi, bagno 3500\u20AC\u2026"}
          style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 12, outline: 'none', resize: 'vertical', minHeight: 72, fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ds-text)', boxSizing: 'border-box', marginBottom: 12 }}
        />
        <button
          onClick={() => setShowAI(true)}
          style={{ padding: '9px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
        >
          Genera con AI
        </button>
      </div>

      {/* Modal AI placeholder */}
      {showAI && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth: 380, padding: '32px', textAlign: 'center', boxShadow: CARD_SHADOW }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><Bot size={48} color="var(--brand)" strokeWidth={2} /></div>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>{"Funzionalità in arrivo"}</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.6 }}>Disponibile con Sprint 3 (AI Engine + Celery + Claude API)</p>
            <button onClick={() => setShowAI(false)} style={{ padding: '9px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Chiudi</button>
          </div>
        </div>
      )}
    </div>
  );
}
